/**
 * Integrytas Group — Record Extension data access.
 *
 * Loads everything a manifest (see record_extensions.ts) needs to render into a
 * host list page: the 1:1 sister rows, the label maps that turn every code/FK
 * into a human name (§17.B4 — resolved at FETCH time, so no raw id can reach the
 * renderer), and the read-only related-children collections.
 *
 * Sizing note: a sister table is 1:1 with a host entity whose full row set the
 * host page has already loaded, so it is fetched whole (paged past the PostgREST
 * cap by fetchAllRows) rather than filtered by an `.in(...)` list of ids — same
 * cardinality, no URL-length ceiling, one predictable query.
 */

import { apiDb } from './api_client';
import { fetchAllRows } from './fetch_all_rows';
import { fetchLookupNames } from './use_lookup_names';
import {
  lookupCodeColumn,
  type ExtensionChild,
  type ExtensionField,
  type RecordExtension,
} from './record_extensions';
import type { RelatedChildItem } from '../components/common/RelatedChildrenPanel';
import type { PartyAddress } from '../components/common/AddressEditor';

/** Everything one extension contributes to a host LIST page. */
export interface ExtensionListData {
  /** Sister row keyed by the host record's pk (stringified). */
  rows: Record<string, Record<string, unknown>>;
  /** Per field key: raw value (stringified) → display label. */
  labels: Record<string, Record<string, string>>;
  /** Per child key: host pk (stringified) → related items. */
  children: Record<string, Record<string, RelatedChildItem[]>>;
}

const EMPTY: ExtensionListData = { rows: {}, labels: {}, children: {} };

function pageAll(table: string, columns: string, orderCol: string) {
  return fetchAllRows<Record<string, unknown>>((from, to) =>
    apiDb.from(table).select(columns).order(orderCol).range(from, to));
}

async function loadFieldLabels(
  field: ExtensionField,
  locale: string,
): Promise<[string, Record<string, string>] | null> {
  if (field.kind === 'lookup') {
    const codeCol = lookupCodeColumn(field.lookup, field.key);
    const map = await fetchLookupNames(field.lookup.table, field.lookup.tl, locale, { codeCol });
    return [field.key, map];
  }
  if (field.kind === 'fk') {
    const rows = await pageAll(field.ref.table, `${field.ref.pk}, ${field.ref.label_column}`, field.ref.pk);
    const map: Record<string, string> = {};
    for (const r of rows) map[String(r[field.ref.pk])] = String(r[field.ref.label_column] ?? '');
    return [field.key, map];
  }
  return null;
}

async function loadRelatedChild(
  child: Extract<ExtensionChild, { kind: 'related' }>,
): Promise<Record<string, RelatedChildItem[]>> {
  const rows = await pageAll(child.table, `${child.pk}, ${child.fk}, ${child.label_column}`, child.pk);
  const byHost: Record<string, RelatedChildItem[]> = {};
  for (const r of rows) {
    const host = String(r[child.fk]);
    const item: RelatedChildItem = { id: r[child.pk] as number, label: String(r[child.label_column] ?? '') };
    byHost[host] = [...(byHost[host] ?? []), item];
  }
  return byHost;
}

async function loadRelatedViaChild(
  child: Extract<ExtensionChild, { kind: 'related_via' }>,
  locale: string,
): Promise<Record<string, RelatedChildItem[]>> {
  const [links, names] = await Promise.all([
    pageAll(child.junction, `${child.junction_pk}, ${child.fk}, ${child.target_fk}`, child.junction_pk),
    fetchLookupNames(child.target.table, child.target.tl, locale, { codeCol: child.target.pk }),
  ]);
  const byHost: Record<string, RelatedChildItem[]> = {};
  for (const r of links) {
    const host = String(r[child.fk]);
    const target = String(r[child.target_fk]);
    // §17.B4: the resolved name is the label; the raw key rides along as hover text.
    const item: RelatedChildItem = { id: r[child.junction_pk] as number, label: names[target] ?? target, title: target };
    byHost[host] = [...(byHost[host] ?? []), item];
  }
  return byHost;
}

/**
 * Load one extension's list-page data. Fails OPEN (§1.5): a missing table, an
 * RLS denial or a dropped module yields empty data rather than breaking the host
 * page — the extension's columns simply render blank.
 */
export async function loadExtensionListData(
  ext: RecordExtension,
  locale: string,
): Promise<ExtensionListData> {
  try {
    const sisterCols = ['*'].join(',');
    const [sisterRows, labelPairs, childEntries] = await Promise.all([
      pageAll(ext.sister.table, sisterCols, ext.sister.pk),
      Promise.all(ext.fields.map(f => loadFieldLabels(f, locale))),
      Promise.all((ext.children ?? [])
        .filter(c => c.kind !== 'address')
        .map(async c => {
          const items = c.kind === 'related'
            ? await loadRelatedChild(c)
            : await loadRelatedViaChild(c as Extract<ExtensionChild, { kind: 'related_via' }>, locale);
          return [c.key, items] as const;
        })),
    ]);

    const rows: Record<string, Record<string, unknown>> = {};
    for (const r of sisterRows) rows[String(r[ext.sister.pk])] = r;

    return {
      rows,
      labels: Object.fromEntries(labelPairs.filter((p): p is [string, Record<string, string>] => p !== null)),
      children: Object.fromEntries(childEntries),
    };
  } catch {
    return EMPTY;
  }
}

/** An address child row, carrying its own PK so a save can replace it. */
export type ExtensionAddressRow = PartyAddress & { __pk?: number };

/** One host record's extension state, for the edit modal. */
export interface ExtensionRecordData {
  /** The sister row, or null when this host record has no extension row yet. */
  row: Record<string, unknown> | null;
  /** Address rows per address-child key. */
  addresses: Record<string, ExtensionAddressRow[]>;
}

/** Select options (raw value → label) for every lookup/fk field in a manifest. */
export async function loadExtensionOptions(
  ext: RecordExtension,
  locale: string,
): Promise<Record<string, Record<string, string>>> {
  try {
    const pairs = await Promise.all(ext.fields.map(f => loadFieldLabels(f, locale)));
    return Object.fromEntries(pairs.filter((p): p is [string, Record<string, string>] => p !== null));
  } catch {
    return {};
  }
}

/** The sister row + address children for ONE host record. Fails open (§1.5). */
export async function loadExtensionRecord(
  ext: RecordExtension,
  pk: string | number,
): Promise<ExtensionRecordData> {
  try {
    const addressChildren = (ext.children ?? []).filter(
      (c): c is Extract<ExtensionChild, { kind: 'address' }> => c.kind === 'address',
    );
    const [sister, addressEntries] = await Promise.all([
      apiDb.from(ext.sister.table).select('*').eq(ext.sister.pk, pk),
      Promise.all(addressChildren.map(async c => {
        const { data } = await apiDb
          .from(c.table)
          .select(`${c.pk}, address_type, line1, line2, city, county_state, postcode, country`)
          .eq(c.fk, pk);
        const rows = ((data ?? []) as unknown as Record<string, unknown>[]).map(r => ({
          __pk: r[c.pk] as number,
          address_type: String(r.address_type ?? ''),
          line1: String(r.line1 ?? ''),
          line2: (r.line2 as string | null) ?? '',
          city: String(r.city ?? ''),
          county_state: (r.county_state as string | null) ?? '',
          postcode: (r.postcode as string | null) ?? '',
          country: (r.country as string | null) ?? '',
        }));
        return [c.key, rows] as const;
      })),
    ]);
    const rows = (sister.data ?? []) as unknown as Record<string, unknown>[];
    return { row: rows[0] ?? null, addresses: Object.fromEntries(addressEntries) };
  } catch {
    return { row: null, addresses: {} };
  }
}

/**
 * Display text for one extension cell. Y/N is returned as the raw flag so the
 * caller can render a <YesNoBadge> (§17.B10); every other kind resolves to a
 * label, never a raw code or id (§17.B4).
 */
export function extensionCellText(
  field: ExtensionField,
  value: unknown,
  labels: Record<string, string> | undefined,
): string {
  if (value === null || value === undefined || value === '') return '';
  if (field.kind === 'lookup' || field.kind === 'fk') return labels?.[String(value)] ?? '';
  return String(value);
}

/** Ascending comparator for an extension column (the host applies direction). */
export function extensionCompare(
  field: ExtensionField,
  a: unknown,
  b: unknown,
  labels: Record<string, string> | undefined,
): number {
  const av = extensionCellText(field, a, labels);
  const bv = extensionCellText(field, b, labels);
  if (av === '' && bv === '') return 0;
  if (av === '') return 1;              // empties sort last, matching useUdcGrid
  if (bv === '') return -1;
  if (field.kind === 'number') return Number(a) - Number(b);
  return av.localeCompare(bv);
}
